package presentation.screens.login

import analytics.Analytics
import com.kinoshazam.Navigator
import com.kinoshazam.analytics.AppEvents
import com.kinoshazam.data.PendingAuthCredentials
import com.kinoshazam.data.TokenRepository
import com.kinoshazam.data.models.LoginResult
import com.kinoshazam.data.models.LoginResult.LoginError
import com.kinoshazam.data.models.RegistrationResult
import com.kinoshazam.model.Route
import data.LoginRepository
import org.koin.core.component.KoinComponent
import presentation.screens.login.LoginState.ErrorType
import presentation.screens.otp.OtpState.OtpFlowType
import utils.BaseViewModel
import utils.Patterns
import utils.launch

class LoginViewModel(
    private val loginRepository: LoginRepository,
    private val tokenRepository: TokenRepository,
    private val pendingAuthCredentials: PendingAuthCredentials,
    private val navigator: Navigator,
    private val analytics: Analytics,
) : BaseViewModel<LoginState, LoginMVIAction>(), KoinComponent {

    override fun initState(): LoginState = LoginState()

    override fun onAction(action: LoginMVIAction) {
        when (action) {
            is LoginMVIAction.EmailChanged -> onLoginChanged(action.email)
            is LoginMVIAction.PasswordChanged -> onPasswordChanged(action.password)
            is LoginMVIAction.HidePasswordClicked -> updateState { copy(passwordVisible = !passwordVisible) }
            is LoginMVIAction.LoginClicked -> onLoginClicked()
            is LoginMVIAction.BackPressed -> navigator.back()
            is LoginMVIAction.ForgotPasswordClicked -> onForgotPasswordClicked()
            is LoginMVIAction.ErrorDismissed -> updateState { copy(error = null) }
        }
    }

    private fun onForgotPasswordClicked() {
        navigator.navigateTo(
            Route.Dialog.ForgotPassword(
                email = stateValue.email
            )
        )
    }

    private fun onLoginClicked() {
        if (!isValid()) return
        launch(onError = {
            updateState { copy(error = ErrorType.Unknown, isLoading = false) }
        }) {
            updateState { copy(isLoading = true) }
            val response = loginRepository.signOn(
                login = state.value.email,
                password = state.value.password
            )
            when (response.result) {
                is RegistrationResult.Registered -> {
                    analytics.track(AppEvents.signUpStarted())
                    openVerificationOtp()
                    updateState { copy(isLoading = false) }
                }

                is RegistrationResult.UserExist -> login()
                null -> updateState { copy(error = ErrorType.Unknown, isLoading = false) }
            }
        }
    }

    private fun onLoginChanged(login: String) {
        updateState {
            copy(
                email = login,
                isLoginError = false
            )
        }
    }

    private fun onPasswordChanged(password: String) {
        updateState {
            copy(
                password = password,
                isPasswordError = false
            )
        }
    }

    private suspend fun login() {
        val response = loginRepository.login(
            login = state.value.email,
            password = state.value.password
        )
        updateState { copy(isLoading = false) }
        when (val result = response.result) {
            is LoginResult.LoggedIn -> onLoggedIn(result.refreshToken, result.accessToken)
            is LoginResult.Error -> onLoginError(result.errorType)
            null -> {
                analytics.track(AppEvents.loginFailed("unknown"))
                updateState { copy(error = ErrorType.Credential) }
            }
        }
    }

    private suspend fun onLoggedIn(refreshToken: String, accessToken: String) {
        pendingAuthCredentials.clear()
        tokenRepository.saveTokens(access = accessToken, refresh = refreshToken)
        analytics.track(AppEvents.loginSucceeded())
        navigator.navigateTo(Route.Dialog.Profile, isClearStack = true)
    }

    private fun onLoginError(errorType: LoginError) {
        when (errorType) {
            LoginError.VERIFICATION_ERROR -> {
                analytics.track(AppEvents.loginFailed("verification"))
                openVerificationOtp()
                updateState { copy(error = ErrorType.Verification) }
            }

            LoginError.CREDENTIAL_ERROR -> {
                analytics.track(AppEvents.loginFailed("credential"))
                updateState { copy(error = ErrorType.Credential) }
            }
        }
    }

    private fun openVerificationOtp() {
        pendingAuthCredentials.put(
            email = state.value.email,
            password = state.value.password,
        )
        navigator.navigateTo(
            Route.Dialog.OtpScreen(
                email = state.value.email,
                flowType = OtpFlowType.Verification.name,
            )
        )
    }

    private fun isValid(): Boolean {
        val email = state.value.email
        val isEmailValid = email.isNotEmpty() && Patterns.EMAIL_ADDRESS.matches(email)

        val password = state.value.password
        val isPasswordValid = password.length >= 8 &&
                password.any { it.isDigit() } &&
                password.any { it.isUpperCase() } &&
                password.any { it.isLowerCase() }

        return if (!isEmailValid || !isPasswordValid) {
            updateState { copy(isLoginError = !isEmailValid, isPasswordError = !isPasswordValid) }
            false
        } else {
            true
        }
    }
}
